/**
 * Payment-purpose helpers for the Patient Profile "Collect Payment" modal.
 *
 * A payment collected from Patient Profile is one of two different business
 * events, and Open Dental records them differently:
 *
 *   'balance'  — money against what the patient already owes. The backend runs
 *                buildBalanceAllocation: FIFO paysplits against outstanding
 *                procedures/adjustments, UnearnedType = 0 (earned income).
 *
 *   'deposit'  — a prepayment on a signed treatment plan, taken BEFORE the work
 *                exists as a charge. Sent as `intent: 'prepayment'`, which routes
 *                the backend to buildPrepaymentAllocation: ONE unallocated
 *                paysplit stamped with the site's Cat-29 UnearnedType. This is
 *                the identical OD outcome to a Tx Presenter chairside deposit
 *                (PresentDepositPanel.vue) — the same business event must not
 *                post two different ways depending on which screen took it.
 *
 * These two functions are extracted (rather than living as inline computeds)
 * because both guard the same expensive failure mode: a prepayment sent while
 * the site's `prepaymentUnearnedType` is unset charges the card and then
 * SILENTLY fails the Open Dental write — payment-service/helpers/odWrite.js
 * throws PayTypeResolutionError and writePaymentToODSafe swallows it as
 * OD_WRITE_FAILED. The operator sees success; Open Dental sees nothing.
 */

export const PAYMENT_PURPOSE = Object.freeze({
  BALANCE: 'balance',
  DEPOSIT: 'deposit',
});

/**
 * Decides which purpose is preselected and what the amount field starts at.
 *
 * `amount: null` renders an EMPTY input. That is deliberate for the deposit
 * case — there is no balance to default to, and the modal's `amountValid`
 * (`typeof amount === 'number'`) keeps every method card disabled with "Set a
 * valid amount above" until the operator types a figure. Returning 0 instead
 * would show a misleading "$0.00" pre-filled.
 *
 * @param {{patientBalance: number, depositAvailable: boolean}} opts
 * @returns {{purpose: string, amount: number|null}}
 */
export function resolveCollectPaymentDefaults({ patientBalance, depositAvailable } = {}) {
  const balance = Number(patientBalance);
  const hasBalance = Number.isFinite(balance) && balance > 0;

  // A real balance is the common case — keep today's exact behaviour.
  if (hasBalance) {
    return { purpose: PAYMENT_PURPOSE.BALANCE, amount: balance };
  }

  // No balance to collect. Deposit is the only thing this modal can usefully do.
  if (depositAvailable) {
    return { purpose: PAYMENT_PURPOSE.DEPOSIT, amount: null };
  }

  // No balance AND deposits unavailable (no Moolah, or prepaymentUnearnedType
  // unset). Same dead state as before this feature — but the disabled deposit
  // card now explains why instead of leaving the operator guessing.
  return { purpose: PAYMENT_PURPOSE.BALANCE, amount: 0 };
}

/**
 * Extra fields to merge into a createMoolahPaymentRecord payload.
 *
 * Returns `{}` for a balance payment so the request shape is byte-identical to
 * what shipped before — the backend then defaults PaymentIntent to 'balance'.
 *
 * `treatPlanNum` is OMITTED rather than sent as null when no plan is chosen:
 * payment-service/index.js validates it as a positive integer when present, so
 * a null would be a 400 on every unattached deposit.
 *
 * @param {{purpose: string, treatPlanNum: number|string|null}} opts
 * @returns {{intent?: string, treatPlanNum?: number}}
 */
export function buildDepositIntentFields({ purpose, treatPlanNum } = {}) {
  if (purpose !== PAYMENT_PURPOSE.DEPOSIT) return {};

  const fields = { intent: 'prepayment' };
  const parsed = parseInt(treatPlanNum, 10);
  if (Number.isFinite(parsed) && parsed > 0) fields.treatPlanNum = parsed;
  return fields;
}
